import { Form } from '@mairistem/composants';
import { Combobox, getErrorMessage } from '@mairistem/finances-utils';
import { uniqueId } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import {
	type ElementReferentielImputation,
	type ReferentielImputation,
	rechercherReferentiel,
} from '../../services/simulation/referentielImputation';

// Les codes servent de valeur : c'est ce que filtre l'API
interface OptionReferentiel {
	'data-testid': string;
	key: string;
	text: string;
	value: string;
}

const PER_PAGE = 50;

// Distance (px) du bas de la liste a partir de laquelle la page suivante est
// chargee
const SEUIL_DEFILEMENT = 40;

// Delai avant d'envoyer la saisie a l'API, pour ne pas l'interroger a chaque
// frappe
const DELAI_RECHERCHE = 400;

const getTexte = (element: ReferentielImputation) =>
	element.libelle ? `${element.code} - ${element.libelle}` : element.code;

// Options d'une page : uniquement ce qui n'est pas caduque (garde-fou si la
// route en renvoie malgre caduque=0)
const toOptions = (
	element: ElementReferentielImputation,
	elements: ReferentielImputation[],
): OptionReferentiel[] =>
	elements
		.filter((referentiel) => !referentiel.caduque)
		.map((referentiel) => ({
			'data-testid': `${element}${referentiel.code}`,
			key: referentiel.code,
			text: getTexte(referentiel),
			value: referentiel.code,
		}));

// Ajoute des options sans doublon de code (un meme code peut revenir sur
// plusieurs pages, ex: article present dans deux chapitres)
const fusionner = (
	options: OptionReferentiel[],
	ajouts: OptionReferentiel[],
) => {
	const codes = new Set(options.map((option) => option.value));

	return [
		...options,
		...ajouts.filter((option) => {
			if (codes.has(option.value)) return false;
			codes.add(option.value);
			return true;
		}),
	];
};

interface ArbitrageMasseFiltreReferentielProps {
	'data-testid': string;
	element: ElementReferentielImputation;
	label: string;
	onChange: (codes: string[]) => void;
	// Parametres propres a l'element (exercice ou entite...)
	params: Record<string, string>;
	placeholder: string;
	value: string[];
}

// Filtre a choix multiples sur un element du referentiel comptable. La liste
// est chargee page par page : la premiere a l'ouverture, les suivantes au
// defilement de la liste ; la saisie est transmise a l'API (recherche serveur)
const ArbitrageMasseFiltreReferentiel = ({
	'data-testid': dataTestId,
	element,
	label,
	onChange,
	params,
	placeholder,
	value,
}: ArbitrageMasseFiltreReferentielProps) => {
	const [options, setOptions] = React.useState<OptionReferentiel[]>([]);
	// Derniere page chargee (0 : rien de charge pour la recherche en cours)
	const [page, setPage] = React.useState(0);
	const [lastPage, setLastPage] = React.useState(1);
	const [loading, setLoading] = React.useState(false);
	// Echec du chargement pour le contexte courant : pas de nouvel essai
	// automatique (il reprendra au prochain changement de recherche)
	const [enErreur, setEnErreur] = React.useState(false);
	const [ouvert, setOuvert] = React.useState(false);
	const [recherche, setRecherche] = React.useState('');
	const [rechercheDifferee, setRechercheDifferee] = React.useState('');
	// Options deja choisies, pour garder leur libelle quand elles ne font pas
	// partie des pages chargees (autre recherche, liste pas encore ouverte)
	const selectionnees = React.useRef(new Map<string, OptionReferentiel>());
	const derniereRequete = React.useRef(0);
	// Classe propre a cette liste : le menu est rendu dans un portail, c'est
	// elle qui permet d'y reconnaitre son defilement
	const classe = React.useMemo(() => uniqueId('filtreReferentiel'), []);
	const cleParams = JSON.stringify(params);

	React.useEffect(() => {
		const timeout = setTimeout(
			() => setRechercheDifferee(recherche.trim()),
			DELAI_RECHERCHE,
		);

		return () => clearTimeout(timeout);
	}, [recherche]);

	// Nouveau contexte (parametres ou recherche) : on repart de la premiere
	// page, en ignorant une eventuelle requete encore en cours
	// biome-ignore lint/correctness/useExhaustiveDependencies: cleParams represente params
	React.useEffect(() => {
		derniereRequete.current += 1;
		setOptions([]);
		setPage(0);
		setLastPage(1);
		setLoading(false);
		setEnErreur(false);
	}, [element, cleParams, rechercheDifferee]);

	const charger = async (numeroPage: number) => {
		derniereRequete.current += 1;
		const numeroRequete = derniereRequete.current;

		try {
			setLoading(true);
			const reponse = await rechercherReferentiel(
				element,
				params,
				numeroPage,
				PER_PAGE,
				rechercheDifferee,
			);
			if (numeroRequete !== derniereRequete.current) return;

			const nouvelles = toOptions(element, reponse?.data ?? []);
			setOptions((old) =>
				numeroPage === 1 ? fusionner([], nouvelles) : fusionner(old, nouvelles),
			);
			setPage(numeroPage);
			setLastPage(reponse?.meta?.lastPage ?? numeroPage);
		} catch (e) {
			if (numeroRequete === derniereRequete.current) {
				toast.error(getErrorMessage(e, `Erreur lors du chargement : ${label}`));
				setEnErreur(true);
			}
		} finally {
			if (numeroRequete === derniereRequete.current) setLoading(false);
		}
	};

	const hasPageSuivante = page > 0 && page < lastPage;

	// Premiere page a l'ouverture (ou apres un changement de contexte)
	// biome-ignore lint/correctness/useExhaustiveDependencies: pas besoin de 'charger'
	React.useEffect(() => {
		if (ouvert && page === 0 && !loading && !enErreur) charger(1);
	}, [ouvert, page, loading, enErreur]);

	// Etat courant pour l'ecouteur de defilement (qui n'est pas recree a
	// chaque rendu)
	const etat = React.useRef({ enErreur, hasPageSuivante, loading, page });
	etat.current = { enErreur, hasPageSuivante, loading, page };

	const getMenu = React.useCallback(
		() => document.querySelector<HTMLElement>(`.${classe} .menu`),
		[classe],
	);

	// Page suivante quand le bas de la liste approche. Le defilement ne
	// remonte pas dans le DOM : on l'ecoute en phase de capture sur le
	// document, puis on verifie qu'il s'agit bien du menu de cette liste.
	// biome-ignore lint/correctness/useExhaustiveDependencies: pas besoin de 'charger'
	React.useEffect(() => {
		if (!ouvert) return undefined;

		const handleScroll = (e: Event) => {
			const menu = e.target as HTMLElement;
			if (menu !== getMenu()) return;

			const {
				hasPageSuivante: suite,
				loading: enCours,
				page: courante,
			} = etat.current;
			const presDuBas =
				menu.scrollTop + menu.clientHeight >=
				menu.scrollHeight - SEUIL_DEFILEMENT;

			if (presDuBas && suite && !enCours && !etat.current.enErreur) {
				charger(courante + 1);
			}
		};

		document.addEventListener('scroll', handleScroll, true);

		return () => {
			document.removeEventListener('scroll', handleScroll, true);
		};
	}, [ouvert, getMenu]);

	// Si la page chargee ne remplit pas la liste, il n'y a pas d'ascenseur
	// pour declencher la suite : on la charge directement
	// biome-ignore lint/correctness/useExhaustiveDependencies: pas besoin de 'charger'
	React.useEffect(() => {
		if (!ouvert || loading || enErreur || !hasPageSuivante) return;

		const menu = getMenu();
		if (menu && menu.scrollHeight <= menu.clientHeight) charger(page + 1);
	}, [ouvert, loading, enErreur, hasPageSuivante, options, page, getMenu]);

	// Les options choisies restent affichables meme hors des pages chargees
	const optionsAffichees = fusionner(
		options,
		value
			.map((code) => selectionnees.current.get(code))
			.filter((option): option is OptionReferentiel => Boolean(option)),
	);

	const handleChange = (codes: string[]) => {
		codes.forEach((code) => {
			const option = optionsAffichees.find((item) => item.value === code);
			if (option) selectionnees.current.set(code, option);
		});
		// La Combobox vide sa saisie apres un choix : la recherche suit
		setRecherche('');
		onChange(codes);
	};

	return (
		<Form.Field data-testid={dataTestId}>
			{/* biome-ignore lint/a11y/noLabelWithoutControl: meme presentation que les autres labels du formulaire */}
			<label>{label}</label>
			<Combobox
				// Portee aussi par le portail du menu (cf getMenu)
				className={classe}
				fluid
				loading={loading}
				menuPortalTarget={document.body}
				multiple
				noResultsMessage={loading ? 'Chargement...' : 'Aucun résultat'}
				onChange={(_e, { value: codes }) => handleChange(codes as string[])}
				onClose={() => setOuvert(false)}
				onOpen={() => setOuvert(true)}
				onSearchChange={(_e, { searchQuery }) => setRecherche(searchQuery)}
				options={optionsAffichees}
				placeholder={placeholder}
				// Recherche faite par l'API : pas de filtrage supplementaire des
				// options deja chargees
				search={(items) => items}
				searchQuery={recherche}
				selection
				value={value}
			/>
		</Form.Field>
	);
};

export default ArbitrageMasseFiltreReferentiel;
